import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "../../layout";
import {

  Users,
  Zap,
  Sparkles,
} from "lucide-react";
import { motion } from "framer-motion";
import WellnessActivityDetail from "./WellnessActivityDetail";
import BubblePopGame from "../games/BubblePopGame";
import PatternDrawingGame from "../games/PatternDrawingGame";
import ColorByEmotionsGame from "../games/ColorByEmotionsGame";
import GratitudeGardenGame from "../games/GratitudeGardenGame";
import {
  wellnessActivities,
  mentalHealthQuotes,
  stressReliefGames,
} from "./advancedHub";

const MentalHealthHub = () => {
  const [searchParams] = useSearchParams();
  const activityParam = searchParams.get("activity");

  const [breathingExercise, setBreathingExercise] = useState({
    phase: "inhale",
    count: 4,
    isActive: false,
    name: "Breathing Exercise",
  });
  const [currentView, setCurrentView] = useState("main");
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(
    Math.floor(Math.random() * mentalHealthQuotes.length)
  );

  // Check for URL parameters and auto-load activity
  useEffect(() => {
    if (activityParam) {
      // Check localStorage for activity data first
      const storedActivity = localStorage.getItem("selectedActivity");
      if (storedActivity) {
        try {
          const parsedActivity = JSON.parse(storedActivity);
          setSelectedFeature(parsedActivity);
          setCurrentView("activity-detail");
          // Clear the stored activity after loading
          localStorage.removeItem("selectedActivity");
          return;
        } catch (error) {
          console.error("Error parsing stored activity:", error);
        }
      }

      // Fallback: find activity from wellnessActivities array
      const activity = wellnessActivities.find((a) => a.id === activityParam);
      if (activity) {
        setSelectedFeature(activity);
        setCurrentView("activity-detail");
      }
    }
  }, [activityParam]);

  // Complete activity handler
  const handleActivityComplete = () => {
    handleBack();
  };

  useEffect(() => {
    let interval;
    if (breathingExercise.isActive) {
      interval = setInterval(() => {
        setBreathingExercise((prev) => {
          if (prev.count > 1) {
            return { ...prev, count: prev.count - 1 };
          } else {
            const phases = ["inhale", "hold", "exhale", "hold"];
            const currentPhaseIndex = phases.indexOf(prev.phase);
            const nextPhaseIndex = (currentPhaseIndex + 1) % phases.length;
            const nextPhase = phases[nextPhaseIndex];
            const nextCount = prev.pattern
              ? prev.pattern[nextPhaseIndex] || prev.pattern[0]
              : 4;

            return {
              ...prev,
              phase: nextPhase,
              count: nextCount,
            };
          }
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [breathingExercise.isActive]);

  const handleFeatureClick = (feature) => {
    setSelectedFeature(feature);
    setCurrentView("activity-detail");
  };

  const handleGameClick = (gameName) => {
    setSelectedGame(gameName);
    setCurrentView("game");
  };

  const handleBack = () => {
    setCurrentView("main");
    setSelectedFeature(null);
    setSelectedGame(null);
  };

  const getNewQuote = () => {
    let newIndex;
    do {
      newIndex = Math.floor(Math.random() * mentalHealthQuotes.length);
    } while (newIndex === currentQuoteIndex && mentalHealthQuotes.length > 1);
    setCurrentQuoteIndex(newIndex);
  };

  if (currentView === "activity-detail" && selectedFeature) {
    return (
      <Layout>
        <WellnessActivityDetail
          activity={selectedFeature}
          onBack={handleBack}
          onComplete={handleActivityComplete}
        />
      </Layout>
    );
  }

  if (currentView === "game" && selectedGame) {
    const handleGameComplete = () => {
      handleBack();
    };

    switch (selectedGame) {
      case "Bubble Pop Meditation":
        return (
          <Layout>
            <BubblePopGame
              onBack={handleBack}
              onComplete={handleGameComplete}
            />
          </Layout>
        );
      case "Pattern Drawing":
        return (
          <Layout>
            <PatternDrawingGame
              onBack={handleBack}
              onComplete={handleGameComplete}
            />
          </Layout>
        );
      case "Color by Emotions":
        return (
          <Layout>
            <ColorByEmotionsGame
              onBack={handleBack}
              onComplete={handleGameComplete}
            />
          </Layout>
        );
      case "Gratitude Garden":
        return (
          <Layout>
            <GratitudeGardenGame
              onBack={handleBack}
              onComplete={handleGameComplete}
            />
          </Layout>
        );
      default:
        setCurrentView("main");
        return null;
    }
  }

  return (
    <Layout>
      <div className="min-h-screen p-6">
        <div className="max-w-8xl mx-auto">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 sm:p-6 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
                <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900">
                  Wellness Hub
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                  Interactive wellness tools and activities for your mental
                  health journey
                </p>
                <p className="text-xs text-gray-600 sm:hidden">
                  Your wellness journey
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-6 sm:space-y-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-xl border border-white/20">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 text-purple-500" />
                Progressive Wellness Journey
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {wellnessActivities.map((activity, index) => (
                  <motion.div
                    key={activity.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border bg-gradient-to-br from-white to-gray-50 border-white/20 hover:shadow-xl hover:scale-105 cursor-pointer transition-all"
                    onClick={() => handleFeatureClick(activity)}
                  >
                    <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">
                      {activity.icon}
                    </div>
                    <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-2">
                      {activity.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {activity.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-xl border border-white/20">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                <Users className="w-6 h-6 sm:w-7 sm:h-7 text-green-500" />
                Interactive Stress Relief Games
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {stressReliefGames.map((game, index) => (
                  <motion.div
                    key={game.name}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl p-4 hover:shadow-lg transition-all cursor-pointer group hover:scale-105"
                    onClick={() => handleGameClick(game.name)}
                  >
                    <div className="text-3xl mb-3 group-hover:scale-110 transition-transform">
                      {game.icon}
                    </div>
                    <h3 className="font-bold text-gray-800 mb-2">
                      {game.name}
                    </h3>
                    <div className="flex justify-between items-center text-xs mb-3"></div>
                    <div className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white text-center py-2 rounded-lg text-sm font-medium hover:from-purple-600 hover:to-pink-600 transition-colors">
                      Click to Play
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MentalHealthHub;
